import { Component, createRef } from "react";
import { NamingConvention } from "namirasoft-core";
import { NSARouterMakerResult, NSABoxSecret } from "namirasoft-account-react";
import { GitHubCreateRow, GitHubRow, GitHubType } from "namirasoft-credential";
import { NSBox, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { CredentialEntities } from "../CredentialEntities";
import { CredentialLayout } from '../CredentialLayout';
import { CredentialProps } from "../CredentialProps";

interface GitHubEditPageProps extends NSARouterMakerResult, CredentialProps
{
    isEdit: boolean;
}

interface GitHubEditPageState
{
    type?: GitHubType;
}

export default class GitHubEditPage extends Component<GitHubEditPageProps, GitHubEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxString_Host = createRef<NSBoxString>();
    NSABoxSecret_Username = createRef<NSABoxSecret>();
    NSBoxEnum_Type = createRef<NSBoxEnum<GitHubType>>();
    NSABoxSecret_Password = createRef<NSABoxSecret>();
    NSABoxSecret_PATClassicToken = createRef<NSABoxSecret>();
    NSABoxSecret_PATFineGrainedToken = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthAppClientID = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthAppClientSecret = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthAppAccessToken = createRef<NSABoxSecret>();
    NSABoxSecret_OAuthAppRefreshToken = createRef<NSABoxSecret>();
    NSABoxSecret_GithubAppInstallationID = createRef<NSABoxSecret>();
    NSABoxSecret_GithubAppJWT = createRef<NSABoxSecret>();
    NSABoxSecret_GithubAppToken = createRef<NSABoxSecret>();
    NSABoxSecret_SSHPublicKey = createRef<NSABoxSecret>();
    NSABoxSecret_SSHPrivateKey = createRef<NSABoxSecret>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: GitHubEditPageProps)
    {
        super(props);
        this.state = {}
    }

    override render()
    {
        return (
            <CredentialLayout<GitHubRow, GitHubCreateRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to edit a GitHub credential by updating its name, token, or description. The token is kept as a protected secret, and any application set up to use the credential applies the updated value." : "This section allows you to create a GitHub credential that stores a personal access token (PAT) for authenticating to GitHub repositories and source control by entering a name and the token. Namirasoft encrypts the sensitive data for secure access, and any Namirasoft application you set up to use it can then clone, read, and manage your repositories.",
                }}
                pages={{
                    entity: CredentialEntities.git_hub(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): GitHubCreateRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let host = this.NSBoxString_Host.current?.getValue() ?? "";
                                        let username = this.NSABoxSecret_Username.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(GitHubType, GitHubType.Password) ?? GitHubType.Password;
                                        let password = this.NSABoxSecret_Password.current?.getValue() ?? "";
                                        let pat_classic_token = this.NSABoxSecret_PATClassicToken.current?.getValue() ?? "";
                                        let pat_fine_graned_token = this.NSABoxSecret_PATFineGrainedToken.current?.getValue() ?? "";
                                        let oauthapp_client_id = this.NSABoxSecret_OAuthAppClientID.current?.getValue() ?? "";
                                        let oauthapp_client_secret = this.NSABoxSecret_OAuthAppClientSecret.current?.getValue() ?? "";
                                        let oauthapp_access_token = this.NSABoxSecret_OAuthAppAccessToken.current?.getValue() ?? "";
                                        let oauthapp_refresh_token = this.NSABoxSecret_OAuthAppRefreshToken.current?.getValue() ?? "";
                                        let githubapp_installation_id = this.NSABoxSecret_GithubAppInstallationID.current?.getValue() ?? "";
                                        let githubapp_jwt = this.NSABoxSecret_GithubAppJWT.current?.getValue() ?? "";
                                        let githubapp_token = this.NSABoxSecret_GithubAppToken.current?.getValue() ?? "";
                                        let ssh_publickey = this.NSABoxSecret_SSHPublicKey.current?.getValue() ?? "";
                                        let ssh_privatekey = this.NSABoxSecret_SSHPrivateKey.current?.getValue() ?? "";
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            host,
                                            username,
                                            type,
                                            password,
                                            pat_classic_token,
                                            pat_fine_graned_token,
                                            oauthapp_client_id,
                                            oauthapp_client_secret,
                                            oauthapp_access_token,
                                            oauthapp_refresh_token,
                                            githubapp_installation_id,
                                            githubapp_jwt,
                                            githubapp_token,
                                            ssh_publickey,
                                            ssh_privatekey,
                                            description,
                                            git_hub_category: [],
                                            git_hub_field: [],
                                            git_hub_tag: [],
                                        };
                                    },
                                    setRow: (row: GitHubRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxString_Host.current?.setValue(row.host);
                                        this.NSABoxSecret_Username.current?.setValue(row.username_secret_id);
                                        this.NSBoxEnum_Type.current?.setValueOne(row.type);
                                        this.setState(() => ({ type: row.type }), () =>
                                        {
                                            this.NSABoxSecret_Password.current?.setValue(row.password_secret_id);
                                            this.NSABoxSecret_PATClassicToken.current?.setValue(row.pat_classic_token_secret_id);
                                            this.NSABoxSecret_PATFineGrainedToken.current?.setValue(row.pat_finegraned_token_secret_id);
                                            this.NSABoxSecret_OAuthAppClientID.current?.setValue(row.oauthapp_client_id_secret_id);
                                            this.NSABoxSecret_OAuthAppClientSecret.current?.setValue(row.oauthapp_client_secret_secret_id);
                                            this.NSABoxSecret_OAuthAppAccessToken.current?.setValue(row.oauthapp_access_token_secret_id);
                                            this.NSABoxSecret_OAuthAppRefreshToken.current?.setValue(row.oauthapp_refresh_token_secret_id);
                                            this.NSABoxSecret_GithubAppInstallationID.current?.setValue(row.githubapp_installation_id_secret_id);
                                            this.NSABoxSecret_GithubAppJWT.current?.setValue(row.githubapp_jwt_secret_id);
                                            this.NSABoxSecret_GithubAppToken.current?.setValue(row.githubapp_token_secret_id);
                                            this.NSABoxSecret_SSHPublicKey.current?.setValue(row.ssh_publickey_secret_id);
                                            this.NSABoxSecret_SSHPrivateKey.current?.setValue(row.ssh_privatekey_secret_id);
                                        });
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        auto_complete="new-password"
                        style={{ width: NSBox.width.double }}
                    />
                                        <NSBoxString
                        {...this.props}
                        ref={this.NSBoxString_Host}
                        title="Host"
                        required
                        style={{ width: NSBox.width.double }}
                    />
                    <NSRow>
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_Username}
                            required
                            title="Username"
                            auto_complete="new-password"
                        />
                        <NSBoxEnum<GitHubType>
                            title="Github Type"
                            multiple={false}
                            required
                            getEnumObject={() => GitHubType}
                            onChanged={(box) => this.setState(() => ({ type: box.getValue() as GitHubType }))}
                            getTitle={(value) => NamingConvention.auto.convert(value, NamingConvention.Pascal_Case_Space)}
                        />
                    </NSRow>
                    {
                        this.state.type === GitHubType.Password &&
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_Password}
                            required
                            title="Password"
                            auto_complete="new-password"
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    {
                        this.state.type === GitHubType.PATClassic &&
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_PATClassicToken}
                            required
                            title="PAT Classic Token"
                            auto_complete="new-password"
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    {
                        this.state.type === GitHubType.PATFineGrained &&
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_PATFineGrainedToken}
                            required
                            title="PAT Fine Grained Token"
                            auto_complete="new-password"
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    {
                        this.state.type === GitHubType.OAuthApp &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthAppClientID}
                                required
                                title="OAuth App Client ID"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthAppClientSecret}
                                required
                                title="OAuth App Client Secret"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthAppAccessToken}
                                required
                                title="OAuth App Access Token"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_OAuthAppRefreshToken}
                                required
                                title="OAuth App Refresh Token"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    {
                        this.state.type === GitHubType.GitHubApp &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_GithubAppInstallationID}
                                required
                                title="Github App Installation ID"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_GithubAppJWT}
                                required
                                title="Github App JWT"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_GithubAppToken}
                                required
                                title="Github App Token"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    {
                        this.state.type === GitHubType.SSH &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_SSHPublicKey}
                                required
                                title="SSH Public Key"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_SSHPrivateKey}
                                required
                                title="SSH Private Key"
                                auto_complete="new-password"
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </CredentialLayout >
        );
    }
}